import { useEffect, useRef, useCallback } from 'react';
import Clarity from '@microsoft/clarity';

interface ClarityUser {
  userId: string;
  sessionId?: string;
  pageId?: string;
  friendlyName?: string;
}

/**
 * Custom hook to manage Microsoft Clarity analytics initialization and tracking.
 * 
 * Initializes Clarity on mount when a project ID is available and provides
 * functions to identify users, set custom tags, and track custom events.
 * 
 * @returns Object containing Clarity utility functions
 */
export const useClarity = () => {
  const isInitialized = useRef(false);

  // Initialize Clarity on mount
  useEffect(() => {
    const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;

    if (!projectId) {
      return;
    }

    if (isInitialized.current) {
      return;
    }

    try {
      Clarity.init(projectId);
      isInitialized.current = true;
      console.log('Clarity: Initialized successfully');
    } catch (error) {
      console.error('Clarity: Failed to initialize', error);
    }
  }, []);

  /**
   * Identify the current user to Clarity for session tracking
   */
  const identifyUser = useCallback((user: ClarityUser) => {
    if (!isInitialized.current) {
      console.warn('Clarity: Cannot identify user - not initialized');
      return;
    }

    try {
      Clarity.identify(user.userId, user.sessionId, user.pageId, user.friendlyName);
    } catch (error) {
      console.error('Clarity: Failed to identify user', error);
    }
  }, []);

  /**
   * Set a custom tag on the Clarity session
   */
  const setTag = useCallback((key: string, value: string | string[]) => {
    if (!isInitialized.current) {
      return;
    }

    try {
      Clarity.setTag(key, value);
    } catch (error) {
      console.error('Clarity: Failed to set tag', error);
    }
  }, []);

  /**
   * Track a custom event in Clarity
   */
  const trackEvent = useCallback((eventName: string) => {
    if (!isInitialized.current) {
      return;
    }

    try {
      Clarity.event(eventName);
    } catch (error) {
      console.error('Clarity: Failed to track event', error);
    }
  }, []);

  return {
    identifyUser,
    setTag,
    trackEvent,
    isInitialized: isInitialized.current,
  };
};

export default useClarity;
